import React from 'react';
import { cn } from '../../utils/cn';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button as ShadcnButton } from '../ui/button';

const TileHeader = ({
  title,
  subtitle,
  children,
  actions,
  controls,
  className = '',
  titleClassName = '',
  subtitleClassName = '',
  layout = 'center', // 'center', 'left', 'between', 'stack'
  size = 'default', // 'sm', 'default', 'lg'
  divider = false,
  sticky = false,
  ...props
}) => {
  const headerClasses = cn(
    'flex-shrink-0 w-full',
    {
      // Layout variations
      'flex flex-col items-center text-center': layout === 'center',
      'flex flex-col items-start text-left': layout === 'left',
      'flex flex-row items-start justify-between': layout === 'between',
      'flex flex-col space-y-3': layout === 'stack',
    },
    {
      // Spacing based on size
      'mb-2': size === 'sm',
      'mb-4': size === 'default',
      'mb-6': size === 'lg',
    },
    {
      // Sticky positioning
      'sticky top-0 z-10 bg-white': sticky,
    },
    {
      // Divider
      'border-b border-gray-200 pb-4': divider,
    },
    className
  );

  const titleClasses = cn(
    'font-semibold text-gray-800 truncate min-w-0',
    {
      // Size variations
      'text-lg': size === 'sm',
      'text-xl': size === 'default',
      'text-2xl': size === 'lg',
    },
    {
      // Layout specific margins - FIXED: Combined duplicate mb-0 conditions
      'mb-0': !subtitle || (subtitle && layout === 'between'),
      'mb-1': subtitle && (layout === 'center' || layout === 'left'),
    },
    titleClassName
  );

  const subtitleClasses = cn(
    'text-gray-600',
    {
      // Size variations
      'text-xs': size === 'sm',
      'text-sm': size === 'default',
      'text-base': size === 'lg',
    },
    subtitleClassName
  );

  // Render title and subtitle based on layout
  const renderTitleSection = () => {
    if (layout === 'between') {
      return (
        <div className="flex-grow min-w-0">
          {title && (
            <h3 className={titleClasses} title={title}>
              {title}
            </h3>
          )}
          {subtitle && (
            <p className={cn(subtitleClasses, 'mt-1')}>
              {subtitle}
            </p>
          )}
        </div>
      );
    }

    return (
      <>
        {title && (
          <h3 className={titleClasses} title={title}>
            {title}
          </h3>
        )}
        {subtitle && (
          <p className={subtitleClasses}>
            {subtitle}
          </p>
        )}
      </>
    );
  };

  // Render controls/actions section
  const renderControlsSection = () => {
    if (!controls && !actions && !children) return null;

    const controlsClasses = cn(
      'flex items-center gap-2',
      {
        'justify-center': layout === 'center',
        'justify-end flex-shrink-0': layout === 'between',
        // FIXED: Combined duplicate justify-start conditions
        'justify-start': layout === 'left' || layout === 'stack',
      },
      {
        'mt-2': (layout === 'center' || layout === 'left') && (title || subtitle),
        // FIXED: Combined duplicate mt-0 conditions  
        'mt-0': layout === 'between' || layout === 'stack',
      }
    );

    return (
      <div className={controlsClasses}>
        {controls}
        {children}
        {actions}
      </div>
    );
  };

  return (
    <div className={headerClasses} {...props}>
      {layout === 'stack' ? (
        <>
          <div>
            {renderTitleSection()}
          </div>
          {renderControlsSection()}
        </>
      ) : (
        <>
          {renderTitleSection()}
          {renderControlsSection()}
        </>
      )}
    </div>
  );
};

// Sub-components for common header elements

// Action Button Group
export const TileHeaderActions = ({ children, className = '', ...props }) => (
  <div 
    className={cn('flex items-center gap-2', className)} 
    {...props}
  >
    {children}
  </div>
);

// Controls Section (for dropdowns, toggles, etc.)
export const TileHeaderControls = ({ children, className = '', ...props }) => (
  <div 
    className={cn('flex items-center gap-3', className)} 
    {...props}
  >
    {children}
  </div>
);

// Dropdown/Select Control
export const TileHeaderSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  className = '',
  disabled = false,
  ...props
}) => (
  <div className={cn('flex items-center gap-2', className)}>
    {label && (
      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
        {label}:
      </label>
    )}
    <Select
      value={value}
      onValueChange={(val) => onChange({ target: { value: val } })}
      disabled={disabled}
    >
      <SelectTrigger className="h-8 px-3 py-1.5 text-sm min-w-[120px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.value ?? option}
            value={String(option.value ?? option)}
          >
            {option.label ?? option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// Toggle Switch
export const TileHeaderToggle = ({
  label,
  checked,
  onChange,
  disabled = false,
  className = '',
  ...props
}) => (
  <label className={cn('flex items-center gap-2 cursor-pointer', className)}>
    <Switch
      checked={checked}
      onCheckedChange={(val) => onChange({ target: { checked: val } })}
      disabled={disabled}
    />
    {label && (
      <span className={cn(
        'text-sm font-medium',
        disabled ? 'text-gray-400' : 'text-gray-700'
      )}>
        {label}
      </span>
    )}
  </label>
);

// Button for header actions
export const TileHeaderButton = ({
  children,
  variant = 'primary', // 'primary', 'secondary', 'outline', 'ghost'
  size = 'sm', // 'xs', 'sm', 'md'
  disabled = false,
  loading = false,
  className = '',
  ...props
}) => {
  // Map TileHeaderButton variants/sizes to shadcn Button equivalents
  const shadcnVariant = variant === 'primary' ? 'default'
    : variant === 'secondary' ? 'secondary'
    : variant === 'outline' ? 'outline'
    : 'ghost';

  const shadcnSize = (size === 'xs' || size === 'sm') ? 'sm' : 'default';

  return (
    <ShadcnButton
      variant={shadcnVariant}
      size={shadcnSize}
      disabled={disabled || loading}
      className={className}
      {...props}
    >
      {loading && (
        <svg
          className="w-4 h-4 mr-2 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </ShadcnButton>
  );
};

export default TileHeader;